
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Application } from '@enums/application.enums';
import { CommonService } from '@shared/services/common.service';
import { HeaderFooterStateService } from '@shared/services/header-footer-state.service';
import { MarketingPageStateService } from '@shared/services/marketing-page-state.service';

/**
 * URLs where promotional landing (marketing home / search under root) is the intended surface.
 * Used to avoid stripping marketing body styles while the user is still on that experience.
 */
export function isPublicPromotionalLandingUrl(url: string): boolean {
  const path = (url?.split('?')[Application.ZERO] || '').replace(/#.*$/, '').trim();
  return path === '/' || path === '' || path.startsWith('/search-result/');
}

@Injectable({
  providedIn: 'root'
})
export class PromotionalMarketingCleanupService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cms: CommonService,
    private readonly marketingPageStateService: MarketingPageStateService,
    private readonly headerFooterStateService: HeaderFooterStateService
  ) {}

  /**
   * Mirrors PromotionalComponent.clearMarketingAndSeoState — body/marketing theme, marketing flags, header/footer tint.
   */
  clearPromotionalMarketingSurfaceState(hadThemeWhiteLabelClass: boolean): void {
    this.marketingPageStateService.setPromotionalHome(false);
    this.marketingPageStateService.setSeoLandingPageVisible(false);
    this.cms.clearMarketingPageStyles(this.document, hadThemeWhiteLabelClass);
    this.headerFooterStateService.updateState({ backgroundColor: null, themeColor: null });
    this.headerFooterStateService.updateFooterState({
      isFooterVisible: false,
      showMarketingCtas: false,
      backgroundColor: null,
      themeColor: null
    });
  }

  /**
   * Clears marketing DOM/state when navigation landed outside public home but SSR or a guard bypass left residue.
   */
  clearIfStrayMarketingOutsidePublicHome(finalUrl: string): void {
    if (isPublicPromotionalLandingUrl(finalUrl)) {
      return;
    }
    const body = this.document.body;
    const hasMarketingBody = body?.classList.contains('marketing-page-class');
    const hasMarketingStyle = !!this.document.getElementById('marketing-page-theme');
    if (hasMarketingBody || hasMarketingStyle) {
      this.clearPromotionalMarketingSurfaceState(false);
    }
  }
}
